import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, 
  TrendingUp, 
  Download, 
  Clock,
  Plus,
  ArrowUpRight,
  Image,
  Shield,
  PlayCircle,
  BookOpen,
  Zap,
  HelpCircle,
  ArrowRight,
  FileText,
  Video,
  RefreshCw,
  X,
  Sparkles
} from 'lucide-react';
import Button from '../../components/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useMockups } from '../../hooks/useSupabase';
import { formatDistanceToNow } from 'date-fns';
import LicenseUpgradeModal from '../../components/LicenseUpgradeModal';

const Dashboard: React.FC = () => {
  const { profile, user } = useAuth();
  const { mockups, loading, refetch } = useMockups();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [showVideoModal, setShowVideoModal] = React.useState(false);
  const [selectedVideo, setSelectedVideo] = React.useState<{ title: string; url: string } | null>(null);

  // Auto-refresh on mount to ensure fresh data
  React.useEffect(() => {
    refetch();
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalMockups = mockups.length;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Filter mockups created in the current month and year
    const thisMonthMockups = mockups.filter(m => {
      const createdDate = new Date(m.created_at);
      return createdDate.getMonth() === currentMonth && 
             createdDate.getFullYear() === currentYear;
    }).length;
    
    const totalDownloads = mockups.reduce((sum, m) => sum + (m.downloads || 0), 0);
    const uniqueTemplates = new Set(mockups.map(m => m.template_id).filter(Boolean)).size;

    return [
      {
        name: 'Total Mockups',
        value: totalMockups.toString(),
        change: thisMonthMockups > 0 ? `+${thisMonthMockups} this month` : 'No change',
        icon: <Package className="w-6 h-6" />,
        changeType: thisMonthMockups > 0 ? 'positive' : 'neutral'
      },
      {
        name: 'Credits Remaining',
        value: (profile?.credits_balance || 0).toString(),
        change: `credits available`,
        icon: <TrendingUp className="w-6 h-6" />,
        changeType: (profile?.credits_balance || 0) > 0 ? 'positive' : 'negative'
      },
      {
        name: 'Downloads',
        value: totalDownloads.toString(),
        change: 'Total downloads',
        icon: <Download className="w-6 h-6" />,
        changeType: 'neutral'
      },
      {
        name: 'Templates Used',
        value: uniqueTemplates.toString(),
        change: 'unique templates',
        icon: <Clock className="w-6 h-6" />,
        changeType: 'neutral'
      }
    ];
  }, [mockups, profile]);

  // Get recent mockups
  const recentMockups = useMemo(() => {
    return mockups.slice(0, 4).map(mockup => ({
      id: mockup.id,
      name: mockup.name,
      template: mockup.template?.name || 'Unknown',
      createdAt: formatDistanceToNow(new Date(mockup.created_at), { addSuffix: true }),
      thumbnail: mockup.mockup_url,
      templateIcon: getTemplateIcon(mockup.template?.category)
    }));
  }, [mockups]);
  
  // Helper function to get template icon
  function getTemplateIcon(category?: string) {
    switch (category) {
      case 'software': return '📦';
      case 'apparel': return '👔';
      case 'branding': return '☕';
      case 'books': return '📕';
      case 'marketing': return '📄';
      case 'packaging': return '🍶';
      default: return '📦';
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-h2 font-heading text-text-dark mb-2">
              Welcome back{(profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name) ? `, ${profile?.full_name || (user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name}` : ''}! 👋
            </h1>
            <p className="text-body text-body-grey">
              Here's what's happening with your mockups today.
            </p>
          </div>
                    <div className="flex items-center gap-3">
            {/* Upgrade Button */}
            <Button 
              variant="primary" 
              size="small"
              onClick={() => setShowUpgradeModal(true)}
              className="flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Upgrade
            </Button>
          {/* Admin Access Button */}
          {(profile?.role === 'admin' || profile?.role === 'super_admin') && (
            <Link to="/admin">
              <Button variant="secondary" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Admin Panel
              </Button>
            </Link>
          )}
        </div>
        </div>
      </div>

      {/* Dashboard Overview */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Dashboard Overview</h2>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-primary-50 rounded-lg text-brand-purple">
                {stat.icon}
              </div>
              <span className={`text-sm font-medium ${
                stat.changeType === 'positive' ? 'text-green-600' : 
                stat.changeType === 'negative' ? 'text-red-600' : 
                'text-gray-600'
              }`}>
                {stat.change}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-text-dark mb-1">{stat.value}</h3>
            <p className="text-sm text-body-grey">{stat.name}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-brand-purple to-brand-blue rounded-2xl p-8 mb-8 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-6 md:mb-0">
            <h2 className="text-h3 font-heading mb-2">
              Ready to create something amazing?
            </h2>
            <p className="text-lg opacity-90">
              Start with our AI-powered mockup generator
            </p>
          </div>
          <Button 
            variant="secondary" 
            size="large"
            onClick={() => {
              // Clear any previous session data
              sessionStorage.clear();
              // Navigate to template selection
              navigate('/dashboard/mockups');
            }}
          >
              <Plus className="w-5 h-5 mr-2" />
              Create New Mockup
            </Button>
        </div>
      </div>

      {/* Recent Mockups */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-text-dark">Recent Mockups</h2>
            <Link to="/dashboard/mockups" className="text-sm text-brand-purple hover:text-brand-purple/80 font-medium flex items-center">
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-purple"></div>
            </div>
          ) : recentMockups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentMockups.map((mockup) => (
                <Link
                  key={mockup.id}
                  to={`/dashboard/mockups/${mockup.id}`}
                  className="group block"
                >
                  <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all group-hover:border-brand-purple/30">
                    <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                      {mockup.thumbnail ? (
                        <img 
                          src={mockup.thumbnail} 
                          alt={mockup.name}
                          className="w-full h-full object-contain rounded-lg"
                        />
                      ) : (
                        <span className="text-4xl">{mockup.templateIcon}</span>
                      )}
                    </div>
                    <h3 className="font-medium text-text-dark truncate mb-1 group-hover:text-brand-purple">
                      {mockup.name}
                    </h3>
                    <p className="text-sm text-body-grey truncate">{mockup.template}</p>
                    <p className="text-xs text-gray-400 mt-2">{mockup.createdAt}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No mockups yet</p>
              <Button 
                variant="primary" 
                size="medium"
                onClick={() => {
                  // Clear any previous session data
                  sessionStorage.clear();
                  // Navigate to template selection
                  navigate('/dashboard/mockups');
                }}
              >
                  Create Your First Mockup
                </Button>
            </div>
          )}
        </div>
      </div>

      {/* Learning Resources Section */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Start Guide */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-green-100 rounded-lg mr-4">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-gray-900">Quick Start Guide</h3>
              <p className="text-sm text-gray-600">Get started in under 5 minutes</p>
            </div>
          </div>
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center text-sm">
              <div className="w-8 h-8 bg-brand-purple/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-brand-purple font-semibold">1</span>
              </div>
              <p className="text-gray-700">Choose a template from our extensive library</p>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-8 h-8 bg-brand-purple/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-brand-purple font-semibold">2</span>
              </div>
              <p className="text-gray-700">Upload your logo or design (PNG/JPG)</p>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-8 h-8 bg-brand-purple/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-brand-purple font-semibold">3</span>
              </div>
              <p className="text-gray-700">Customize colors and download your mockup</p>
            </div>
          </div>
          
          <Button 
            variant="primary" 
            size="small" 
            className="w-full"
            onClick={() => navigate('/dashboard/mockups')}
          >
            Start Creating
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Video Tutorials */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <PlayCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-gray-900">Video Tutorials</h3>
              <p className="text-sm text-gray-600">Learn from our expert tutorials</p>
            </div>
          </div>
          
          <div className="space-y-3 mb-6">
            <button 
              onClick={() => {
                setSelectedVideo({ 
                  title: 'Creating Your First Mockup', 
                  url: 'https://www.youtube.com/embed/dQw4w9WgXcQ' 
                });
                setShowVideoModal(true);
              }}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group w-full text-left"
            >
              <div className="flex items-center">
                <Video className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Creating Your First Mockup</p>
                  <p className="text-xs text-gray-500">3:45 min</p>
                </div>
              </div>
              <PlayCircle className="w-5 h-5 text-gray-400 group-hover:text-brand-purple transition-colors" />
            </button>
            
            <button 
              onClick={() => {
                setSelectedVideo({ 
                  title: 'Advanced Customization', 
                  url: 'https://www.youtube.com/embed/dQw4w9WgXcQ' 
                });
                setShowVideoModal(true);
              }}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group w-full text-left"
            >
              <div className="flex items-center">
                <Video className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Advanced Customization</p>
                  <p className="text-xs text-gray-500">5:12 min</p>
                </div>
              </div>
              <PlayCircle className="w-5 h-5 text-gray-400 group-hover:text-brand-purple transition-colors" />
            </button>
            
            <button 
              onClick={() => {
                setSelectedVideo({ 
                  title: 'Using AI Features', 
                  url: 'https://www.youtube.com/embed/dQw4w9WgXcQ' 
                });
                setShowVideoModal(true);
              }}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group w-full text-left"
            >
              <div className="flex items-center">
                <Video className="w-5 h-5 text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Using AI Features</p>
                  <p className="text-xs text-gray-500">4:28 min</p>
                </div>
              </div>
              <PlayCircle className="w-5 h-5 text-gray-400 group-hover:text-brand-purple transition-colors" />
            </button>
          </div>
          
          <Button variant="secondary" size="small" className="w-full">
            View All Tutorials
            <ArrowUpRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Enhanced Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <button 
          onClick={() => navigate('/dashboard/library')}
          className="p-6 bg-white rounded-xl border border-gray-200 hover:border-brand-purple hover:shadow-lg transition-all group"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Image className="w-6 h-6 text-purple-600" />
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-brand-purple group-hover:translate-x-1 transition-all" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Browse Gallery</h3>
          <p className="text-sm text-gray-600">Explore mockups created by others</p>
        </button>

        <button 
          onClick={() => navigate('/dashboard/assets')}
          className="p-6 bg-white rounded-xl border border-gray-200 hover:border-brand-purple hover:shadow-lg transition-all group"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-brand-purple group-hover:translate-x-1 transition-all" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Manage Assets</h3>
          <p className="text-sm text-gray-600">Upload and organize your designs</p>
        </button>

        <button 
          onClick={() => navigate('/dashboard/support')}
          className="p-6 bg-white rounded-xl border border-gray-200 hover:border-brand-purple hover:shadow-lg transition-all group"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <HelpCircle className="w-6 h-6 text-blue-600" />
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-brand-purple group-hover:translate-x-1 transition-all" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Get Help</h3>
          <p className="text-sm text-gray-600">Contact support or browse FAQs</p>
        </button>
      </div>

      {/* Pro Tips - Redesigned */}
      <div className="mt-8 bg-gradient-to-r from-brand-purple/5 to-brand-blue/5 rounded-xl p-6 border border-gray-100">
        <div className="flex items-center mb-4">
          <Zap className="w-5 h-5 mr-2 text-brand-purple" />
          <h3 className="font-semibold text-gray-900">Pro Tips for Better Mockups</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start">
            <div className="w-2 h-2 bg-brand-purple rounded-full mt-1.5 mr-3 flex-shrink-0"></div>
            <p className="text-gray-700">Use high-resolution logos (at least 1000x1000px) for crisp, professional results</p>
          </div>
          <div className="flex items-start">
            <div className="w-2 h-2 bg-brand-purple rounded-full mt-1.5 mr-3 flex-shrink-0"></div>
            <p className="text-gray-700">Try different background colors to make your designs stand out</p>
          </div>
          <div className="flex items-start">
            <div className="w-2 h-2 bg-brand-purple rounded-full mt-1.5 mr-3 flex-shrink-0"></div>
            <p className="text-gray-700">Download in PNG format for transparent backgrounds and maximum flexibility</p>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{selectedVideo.title}</h3>
              <button
                onClick={() => {
                  setShowVideoModal(false);
                  setSelectedVideo(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="aspect-video bg-black">
              <iframe
                src={selectedVideo.url}
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <LicenseUpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentLicense={profile?.license || 'free'}
      />
      </div>
    </div>
  );
};

export default Dashboard;
